/**
 * knox-config.tsx — shared configuration context for Knox UI.
 *
 * Authentication is handled via HttpOnly cookies (knox_token),
 * so no auth token is stored in localStorage or exposed to JS.
 */

import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { getElevoApiBase, getGatewayUrl, getPreviewBaseUrl } from './debug-mode';

export interface CreditPack {
  credits: number;
  price: number; // 元（国内）或美元（海外）
}

export interface KnoxConfig {
  elevoApiBase: string;
  gatewayUrl: string;
  /** Gating for text body file path auto-detection (knox-file:// links in markdown).
   *  Does NOT gate structured resource_items cards from agent present_result —
   *  those are always clickable. */
  enableFilePathLinks: boolean;
  /** Base URL for secure HTML preview service (separate domain/port for XSS isolation) */
  previewBaseUrl: string;
  /** 可用支付渠道（["alipay"] / ["stripe"] / []），决定 billing UI 是否展示 */
  paymentChannels: string[];
  /** 积分包定价列表（从 GET /api/v1/config 拉取，不硬编码） */
  creditPacks: CreditPack[];
  /** 货币符号（"CNY" / "USD"），决定显示 ¥ 还是 $ */
  currency: string;
  /** 是否已加载（首次拉 GET /api/v1/config 完成前为 false） */
  configLoaded: boolean;
}

const KnoxConfigContext = createContext<KnoxConfig>({
  elevoApiBase: '',
  gatewayUrl: '',
  enableFilePathLinks: false,
  previewBaseUrl: '',
  paymentChannels: [],
  creditPacks: [],
  currency: 'USD',
  configLoaded: false,
});

export function KnoxConfigProvider({ children }: { children: ReactNode }) {
  const [paymentChannels, setPaymentChannels] = useState<string[]>([]);
  const [creditPacks, setCreditPacks] = useState<CreditPack[]>([]);
  const [currency, setCurrency] = useState('USD');
  const [configLoaded, setConfigLoaded] = useState(false);

  // 启动时拉 GET /api/v1/config，拿支付渠道和积分包定价
  useEffect(() => {
    fetch('/api/v1/config')
      .then(res => res.json())
      .then(data => {
        setPaymentChannels(data.payment_channels ?? []);
        setCreditPacks(data.credit_packs ?? []);
        setCurrency(data.currency ?? 'USD');
      })
      .catch(err => {
        // config 接口失败不阻塞应用，默认不展示支付 UI
        console.warn('Failed to load /api/v1/config:', err);
      })
      .finally(() => setConfigLoaded(true));
  }, []);

  const config: KnoxConfig = {
    elevoApiBase: getElevoApiBase(),
    gatewayUrl: getGatewayUrl(),
    enableFilePathLinks: import.meta.env.VITE_ENABLE_FILE_PATH_LINKS === 'true',
    previewBaseUrl: getPreviewBaseUrl(),
    paymentChannels,
    creditPacks,
    currency,
    configLoaded,
  };

  return (
    <KnoxConfigContext.Provider value={config}>
      {children}
    </KnoxConfigContext.Provider>
  );
}

export function useKnoxConfig(): KnoxConfig {
  return useContext(KnoxConfigContext);
}

/**
 * 计算某个积分包相对第一档的折扣百分比。
 * 以第一档单价为基准（packs[0].price / packs[0].credits），向下取整。
 * 返回 0 表示无折扣（如海外同价档位、或第一档自身）。
 *
 * 示例（国内 CN 定价）：
 *   - 200/15  -> 0%  (基准)
 *   - 500/35  -> 7%
 *   - 1500/89 -> 20%
 *   - 5000/249 -> 33%
 */
export function computeDiscountPercent(pack: CreditPack, packs: CreditPack[]): number {
  if (!packs.length) return 0;
  const base = packs[0];
  if (base.credits <= 0 || base.price <= 0) return 0;
  const baseUnit = base.price / base.credits;
  const packUnit = pack.price / pack.credits;
  if (packUnit >= baseUnit) return 0;
  return Math.floor((1 - packUnit / baseUnit) * 100);
}
